import { FieldConfig } from 'model/deals.model';
import zeplinColor from '../theme/Color';

export class Constants {
  public static readonly ErrorType = {
    NotFound: 'UserNotFoundException',
    NotAuthourized: 'NotAuthorizedException',
    LimitExceeded: 'LimitExceededException',
    ExpiredCode: 'ExpiredCodeException',
  };
  public static readonly ErrorText = {
    ExpiredCodeException: 'Code expired',
    LimitExceededException: 'Attempt limit exceeded. Try again later',
    UserNotFoundException: 'User Not Found',
    NotAuthorizedException: 'Wrong password. Try again or click Forgot password to reset it',
    CodeMismatchException: 'Code not matched.',
  };

  public static readonly PriorityTask = {
    Normal: zeplinColor.Blue,
    Low: zeplinColor.AlertInfo,
    High: zeplinColor.Error,
  };

  public static readonly StatusTask = {
    'To Do': zeplinColor.AlertInfo,
    'In Progress': zeplinColor.Blue,
    Done: zeplinColor.Error,
  };

  public static readonly DealsText = {
    totalDealsWon: 'Monthly Closed Deals',
    totalOpenDeals: 'Monthly Open Deals',
    quaterly: 'Quarterly Revenue',
    totalTasks: 'Tasks Completed',
  };
  public static readonly DealsColor = {
    totalDealsWon: zeplinColor.Blue1,
    totalOpenDeals: zeplinColor.Yellow,
    quaterly: zeplinColor.Green,
    totalTasks: zeplinColor.Blue2,
  };
  public static readonly privacyOptions = [
    {
      label: 'A&A Management Group',
      value: 'public_to_workspace',
      description: 'Everyone in your organization can find and access this project.',
    },
    {
      label: 'Shared with team',
      value: 'private_to_team',
      description: 'Only this team and invited members can find and access this project.',
    },
    {
      label: 'Private to members',
      value: 'private',
      description: 'Only invited members can find and access this project.',
    },
  ];
}

export enum Error {
  NotFound = 'UserNotFoundException',
  NotAuthourized = 'NotAuthorizedException',
  LimitExceeded = 'LimitExceededException',
  ExpiredCode = 'ExpiredCodeException',
  CodeMismatch = 'CodeMismatchException',
}

export enum ClientType {
  Talent = 'Talent',
  Athlete = 'Athlete',
  Business = 'Business Development',
}

export const CALENDAR_PALETTE = [
  zeplinColor.Brand1,
  zeplinColor.Blue5,
  zeplinColor.USER_PROFILE_COLOR1,
  zeplinColor.USER_PROFILE_COLOR2,
  zeplinColor.USER_PROFILE_COLOR4,
  zeplinColor.AlertInfo,
  zeplinColor.USER_PROFILE_COLOR8,
  zeplinColor.USER_PROFILE_COLOR13,
  zeplinColor.USER_PROFILE_COLOR9,
  zeplinColor.USER_PROFILE_COLOR10,
  zeplinColor.USER_PROFILE_COLOR11,
  zeplinColor.USER_PROFILE_COLOR12,
];

export const CALENDAR_PROVIDERS = [
  { label: 'Huddle', type: 'huddle' },
  { label: 'Zoom', type: 'zoom' },
  { label: 'Google Meet', type: 'google' },
  { label: 'Microsoft Teams', type: 'microsoft' },
];

export const StaticDealFormFields: FieldConfig[] = [
  { name: 'section.dealPoints', label: 'Deal Points', type: 'section', xs: 12, sm: 12 },

  { name: 'dealDetails.dealValue', label: 'Deal value (gross)', type: 'text', xs: 12, sm: 6 },
  {
    name: 'dealDetails.commissionRate',
    label: 'Commission rate (%)',
    type: 'number',
    xs: 12,
    sm: 6,
  },

  { name: 'dealDetails.commissionValue', label: 'Commission value', type: 'number', xs: 12, sm: 6 },
  {
    name: 'dealDetails.dealStructure',
    label: 'Deal structure',
    type: 'select',
    xs: 12,
    sm: 6,
  },

  { name: 'dealDetails.equity', label: 'Equity/ownership (%)', type: 'number', xs: 12, sm: 6 },
  { name: 'dealDetails.backend', label: 'Backend', type: 'text', xs: 12, sm: 6 },

  {
    name: 'dealDetails.closeDate',
    label: 'Expected close date',
    type: 'date',
    xs: 12,
    sm: 12,
  },

  { name: 'section.attribution', label: 'Attribution & Tracking', type: 'section', xs: 12, sm: 12 },

  {
    name: 'dealDetails.leadershipSponsor',
    label: 'Leadership sponsor',
    type: 'text',
    xs: 12,
    sm: 6,
  },
  {
    name: 'dealDetails.source',
    label: 'Source',
    type: 'select',
    options: [],
    xs: 12,
    sm: 6,
  },

  { name: 'dealDetails.partnerAccount', label: 'Partner account', type: 'text', xs: 12, sm: 6 },
  { name: 'dealDetails.partnerContact', label: 'Partner contact', type: 'text', xs: 12, sm: 6 },

  {
    name: 'dealDetails.daysInCurrentStage',
    label: 'Days in current stage',
    type: 'text',
    xs: 12,
    sm: 12,
  },

  { name: 'section.additional', label: 'Additional Information', type: 'section', xs: 12, sm: 12 },

  {
    name: 'dealDetails.outcome',
    label: 'Outcome',
    type: 'select',
    options: [],
    xs: 12,
    sm: 6,
  },
  {
    name: 'dealDetails.lossReason',
    label: 'Loss reason',
    type: 'select',
    options: [],
    xs: 12,
    sm: 6,
  },
  {
    name: 'dealDetails.passReason',
    label: 'Pass reason',
    type: 'select',
    options: [],
    xs: 12,
    sm: 6,
  },
  { name: 'dealDetails.competitor', label: 'Competitor', type: 'text', xs: 12, sm: 6 },

  { name: 'section.notes', label: 'Notes', type: 'section', xs: 12, sm: 12 },

  {
    name: 'dealDetails.notes',
    label: 'Notes',
    type: 'textarea',
    multiline: true,
    minRows: 3,
    xs: 12,
    sm: 12,
  },
];

export const OPPORTUNITY_INFO_SECTION: FieldConfig = {
  name: 'section.opportunity',
  label: 'Opportunity Information',
  type: 'section',
};

export const SHARED_TOP_FIELDS: FieldConfig[] = [
  { name: 'dealDetails.name', label: 'Opportunity name', type: 'text', xs: 12, required: true },
  {
    name: 'dealDetails.clientName',
    label: 'Client name',
    type: 'autocomplete',
    autocompleteType: 'client',
    sm: 6,
    required: true,
  },
  {
    name: 'dealDetails.ownerName',
    label: 'Opportunity owner',
    type: 'autocomplete',
    autocompleteType: 'owner',
    sm: 6,
    required: true,
  },
  {
    name: 'dealDetails.probability',
    label: 'Probability (%)',
    type: 'text',
    sm: 6,
    required: true,
  },
];
